import { ReactComponent as Delete } from "assets/icons/delete.svg";
import { ReactComponent as Edit } from "assets/icons/edit.svg";
import { getDisplayDateFormat } from "helpers/DatesHelper";
import { handlePageClick, handlePageSizeChange } from "helpers/PaginationHelper";
import { useResponsiveWidth } from "hooks/useResponsiveWidth";
import {
    Button,
    Checkbox,
    ConfirmationModal,
    CustomTable,
    NavDropdown,
    OptionType,
    Pagination,
    RoleType,
    TableType,
} from "modules/common";
import { useContext, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { SortingRule } from "react-table";
import { Record } from "services/records";
import { RecordResponse } from "services/records/response";
import { RootState } from "store";
import { AccessibilityContext } from "views/AccessibilityContext";
import { downloadFile, useBulkDeleteRecords } from "../hooks";
import { RecordDetailsModal } from "./RecordDetailsModal";

export interface IRecordsTable {
    records?: RecordResponse;
    isLoading: boolean;
    setSortBy: (sortBy: SortingRule<object>) => void;
    pageSize: OptionType<string>;
    setPageSize: React.Dispatch<React.SetStateAction<OptionType<string>>>;
    selectedRows: Record[];
    setSelectedRows: (rows: Record[]) => void;
    currentPage: number;
    setCurrentPage: React.Dispatch<React.SetStateAction<number>>;
}

export const RecordsTable = ({
    records,
    isLoading,
    setSortBy,
    pageSize,
    setPageSize,
    setCurrentPage,
    selectedRows,
    setSelectedRows,
    currentPage,
}: IRecordsTable) => {
    const [modalOpen, setModalOpen] = useState(false);
    const [selectedRow, setSelectedRow] = useState<Record>();
    const accessToken = useSelector((state: RootState) => state.signIn.user?.accessToken);
    const { profile } = useSelector((state: RootState) => state.profile);
    const bulkDelete = useBulkDeleteRecords();
    const [showBulkDelete, setShowBulkDelete] = useState(false);
    const [isChecked, setIsChecked] = useState(false);
    const isMobileOrTablet = useResponsiveWidth();

    const isSuperAdmin = profile?.roleType?.value === RoleType.SuperAdmin;

    const confirmationModalContent = useMemo(() => {
        return (
            <div className="f f-c">
                <span>Jeste li sigurni da želite trajno izbrisati odabrane evidencije?</span>
                {selectedRows.map(row => (
                    <div key={row.name}>{row.name}</div>
                ))}
            </div>
        );
    }, [selectedRows]);

    const { lowVision } = useContext(AccessibilityContext);

    const tableColumns = useMemo(() => {
        const baseTableColumns: any[] = [
            {
                Header: "NAZIV",
                accessor: "name",
                disableSortBy: true,
                width: 10,
                Cell: ({ row }: any) => {
                    const { original } = row;
                    return (
                        <span
                            style={{ cursor: "pointer" }}
                            onClick={() => {
                                setModalOpen(true);
                                setSelectedRow(original);
                                setIsChecked(false);
                            }}
                        >
                            {original.name}
                        </span>
                    );
                },
            },
            {
                Header: "ROK ISPUNJENJA",
                accessor: "deadline",
                disableSortBy: true,
                width: 3,
                Cell: ({ row }: any) => {
                    const { original } = row;
                    return (
                        <span className="modal-name">
                            {getDisplayDateFormat(original.deadline)}
                        </span>
                    );
                },
            },
            {
                Header: "PREUZIMANJE",
                id: "actions",
                disableSortBy: true,
                width: 3,
                className: "actions-cell",
                Cell: ({ row }: any) => {
                    return (
                        <Button
                            size="small"
                            variant="primary-ghost"
                            lowVision={lowVision}
                            onClick={() => {
                                const { original } = row;
                                downloadFile(original.guid, accessToken!, original.fileName);
                            }}
                        >
                            Preuzmi
                        </Button>
                    );
                },
            },
        ];

        const checkboxColumn: any = {
            id: "select",
            Header: () =>
                selectedRows.length > 0 ? (
                    <div className="table-header-dropdown-container">
                        <NavDropdown name="records" label="">
                            <div
                                className="table-header-dropdown-item"
                                onClick={() => {
                                    setSelectedRow(undefined);
                                    setIsChecked(selectedRows.length === 1);
                                    setModalOpen(true);
                                }}
                            >
                                UREDI
                                <Edit />
                            </div>
                            <div
                                className="table-header-dropdown-item"
                                onClick={() => {
                                    setShowBulkDelete(true);
                                }}
                            >
                                OBRIŠI
                                <Delete />
                            </div>
                        </NavDropdown>
                    </div>
                ) : null,
            disableSortBy: true,
            width: 2,
            Cell: ({ row }: any) => {
                const { original } = row;
                return (
                    <Checkbox
                        isChecked={selectedRows.some(row => row.guid === original.guid)}
                        onChange={event => {
                            if (event.target.checked) {
                                setSelectedRows([...selectedRows, original]);
                            } else {
                                setSelectedRows(
                                    selectedRows.filter(row => row.guid !== original.guid)
                                );
                            }
                        }}
                        type="checkbox"
                        htmlFor={original.guid}
                        text=" "
                    />
                );
            },
        };

        return isSuperAdmin ? [checkboxColumn, ...baseTableColumns] : baseTableColumns;
    }, [isSuperAdmin, selectedRows, accessToken, setSelectedRows]);

    const handleBulkDeleteRecords = () => {
        const recordGuids = selectedRows.map(row => row.guid);
        bulkDelete.mutate(recordGuids);
        setSelectedRows([]);
        setShowBulkDelete(false);
    };

    const onPageSelect = (page: number) => {
        setCurrentPage(page);
    };

    return (
        <div className="records-table-wrapper">
            <div className="records-custom-table">
                <CustomTable
                    flex
                    data={records?.items ?? []}
                    columns={tableColumns}
                    loading={isLoading}
                    setSortBy={setSortBy}
                    tableType={TableType.RecordsTable}
                />
            </div>

            {records && records.items.length !== 0 && (
                <Pagination
                    className="mt-24"
                    menuPlacement={isMobileOrTablet ? "top" : "bottom"}
                    currentPage={currentPage ?? 1}
                    numberOfPages={Math.ceil(records.total / parseInt(pageSize.value))}
                    pageNeighbours={1}
                    pageSize={pageSize}
                    onPageSizeChange={v => handlePageSizeChange(v, setPageSize, setCurrentPage)}
                    handlePageClick={e => handlePageClick(e, setCurrentPage)}
                    handlePageSelect={onPageSelect}
                    next={() => setCurrentPage(p => p + 1)}
                    prev={() => setCurrentPage(p => p - 1)}
                    first={() => setCurrentPage(1)}
                    last={() => {
                        setCurrentPage(Math.ceil(records.total / parseInt(pageSize.value)));
                    }}
                />
            )}

            <RecordDetailsModal
                isOpen={modalOpen}
                handleClose={() => {
                    setModalOpen(false);
                    setIsChecked(false);
                }}
                records={selectedRow ? [selectedRow] : selectedRows}
                chosenPage={currentPage}
                onPageChange={setCurrentPage}
                isChecked={isChecked}
            />

            <ConfirmationModal
                isActive={showBulkDelete}
                onConfirm={handleBulkDeleteRecords}
                onCancel={() => setShowBulkDelete(false)}
                confirmText="OBRIŠI"
                cancelText="ODUSTANI"
                confirmButtonVariant="warning-ghost"
                cancelButtonVariant="primary-ghost"
                title="Brisanje datoteka"
                content={confirmationModalContent}
            />
        </div>
    );
};
